import common from "../../common";
import QuestionModule from "../../maths/modules/QuestionModule";
import GelButton from "../gel/GelButton";
import GelHackButton from "../gel/GelHackButton";
import QuestionInputSelect from "./QuestionInputSelect";

export default class QuestionInputSort extends QuestionInputSelect {
    constructor(question, view) {
        super(question, view);

        this._grabPoint = new PIXI.Point();
        this._grabRadius = 24;
        this._grabBound = false;

        this._focusSortTarget = null;

        if (question.module === QuestionModule.MODULE_TUTORIAL) {
            this._question.sort = this._question.values.slice();
            this._question.sort[0] = this._question.sort[1];
            this._question.sort[1] = this._question.values[0];
        } else {
            let k = 200;
            this._question.sort = P3.utils.shuffleArray(this._question.values.slice());
            while (view.isCorrect() && k-- > 0) {
                this._question.sort = P3.utils.shuffleArray(this._question.values.slice());
            }
        }

        this._bindings = {};
        this._bindings.mouseup = (event) => this.onDocumentMouseUp(event);
        this._bindings.mousemove = (event) => this.onDocumentMouseMove(event);
        this._bindings.touchend = (event) => this.onDocumentMouseUp(event);
        this._bindings.touchmove = (event) => this.onDocumentMouseMove(event);
        this._bindings.keydown = (event) => this.onDocumentKeyDown(event);

        document.addEventListener("mouseup", this._bindings.mouseup);
        document.addEventListener("mousemove", this._bindings.mousemove);
        document.addEventListener("touchend", this._bindings.touchend);
        document.addEventListener("touchmove", this._bindings.touchmove);
        document.addEventListener("keydown", this._bindings.keydown);
    }

    init() {
        this.createOptionButtons(this._question.sort);

        for (let i = 0; i < this._buttons.length; ++i) {
            const button = this._buttons[i];
            button.refocusable = true;
        }

        super.init();
    }

    destroy() {
        document.removeEventListener("mouseup", this._bindings.mouseup);
        document.removeEventListener("mousemove", this._bindings.mousemove);
        document.removeEventListener("touchend", this._bindings.touchend);
        document.removeEventListener("touchmove", this._bindings.touchmove);
        document.removeEventListener("keydown", this._bindings.keydown);

        super.destroy();
    }

    update(dt) {
        super.update(dt);

        const touch = P3.input.mouse && this.toLocal(P3.input.mouse, common.stage);

        const ease = 10;
        const count = this._buttons.length;
        for (let i = 0; i < count; ++i) {
            const button = this._buttons[i];
            if (touch && button.grabbed) {
                button.x += (touch.x - button.x) * ease * dt;
                button.y += (touch.y - button.y) * ease * dt;

                const index = P3.math.clamp(Math.floor(((button.x + this._spacing * 0.5) + (this._spacing * (count - 1) * 0.5)) / this._spacing), 0, count - 1);
                if (index !== button.index) {
                    const from = button.index;
                    button.index = index;
                    this.sort(button.index, from);
                }
            } else {
                button.x += (((this._spacing * button.index) - (this._spacing * (count - 1)) * 0.5) - button.x) * ease * dt;
                button.y += -button.y * ease * dt;
            }
        }
    }

    sort(to, from) {
        let button;
        for (let i = 0; i < this._buttons.length; ++i) {
            if (this._buttons[i].value === this._question.sort[to]) {
                button = this._buttons[i];
                break;
            }
        }
        const value = this._question.sort[button.index];
        if (!this._question.sort[from]) {
            button.index = from;
            this._question.sort[to] = null;
            this._question.sort[from] = value;
        }

        for (let i = 0; i < this._buttons.length; ++i) {
            const button = this._buttons[i];
            button.tabIndex = GelButton.TAB_INDEX_LEVEL_2 + button.index;
        }
    }

    createOptionButton(value) {
        const a = P3.assets;

        const buttonStates = new P3.ButtonStates();
        buttonStates.normal = a.texture("ui_drop_in_area");
        buttonStates.over = a.texture("ui_drop_in_area_highlight");

        const button = !PIXI.utils.isMobile.any ?
            new GelHackButton(buttonStates) :
            new P3.Button(buttonStates);

        button.value = value;
        button.animate = false;
        button.animateIcon = false;
        button.tint = common.config.debug && button.value === this._question.answer ? 0x00AA00 : 0xFFFFFF;

        button.label = new PIXI.Text(button.value.toString(), {
            fontSize: this._fontSize, fontFamily: "Arial", fontWeight: "bold", fill: 0x0});
        button.label.position.set(0, 0);
        button.label.anchor.set(0.5, 0.5);
        button.label.resolution = 2;
        button.animation.addChild(button.label);
        return button;
    }

    showCorrectAnswer() {
        // empty
    }

    grab(button) {
        button.grabbed = true;
        button.parent.addChild(button);

        for (let i = 0; i < this._buttons.length; ++i)
            this._buttons[i].interactive = false;

        this._selected = button;
        this._selected.interactive = true;

        const index = this._question.sort.indexOf(button.value);
        this._question.sort[index] = null;

        P3.audio.playSound("sfx_block_move_00");
    }

    release() {
        this._focusSortTarget = null;

        if (!this._selected) return;
        this._selected.grabbed = false;

        for (let i = 0; i < this._buttons.length; ++i)
            this._buttons[i].interactive = true;

        const count = this._buttons.length;
        const index = P3.math.clamp(Math.floor(((this._selected.x + this._spacing * 0.5) + (this._spacing * (count - 1) * 0.5)) / this._spacing), 0, count - 1);
        this._question.sort[index] = this._selected.value;

        this._selected = null;

        P3.audio.playSound("sfx_block_placed_01");
    }

    initTutorial() {
        super.initTutorial();

        let button = this._buttons[0];

        if (button) {
            this._tutorialHandStart = new PIXI.Point(button.x + 40, button.y + 120);
            this._tutorialHandAnchor = new PIXI.Point(button.x + 40, button.y + 40);
            this._tutorialHandEnd = new PIXI.Point(button.x, button.y);
        }
    }

    animateTutorial() {
        this._tutorialHandTweens.removeAll();

        const a = new P3.utils.TWEEN.Tween(this._tutorialHand, this._tutorialHandTweens)
            .delay(200)
            .onStart(() => {
                this._tutorialHand.position.set(this._tutorialHandStart.x, this._tutorialHandStart.y);
                this._tutorialHand.alpha = 0;
                this._tutorialHand.gotoAndStop("default");
            });

        const b = new P3.utils.TWEEN.Tween(this._tutorialHand, this._tutorialHandTweens)
            .to({x: [this._tutorialHandAnchor.x, this._tutorialHandEnd.x], y: [this._tutorialHandAnchor.y, this._tutorialHandEnd.y], alpha: 1}, 640)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .interpolation(P3.utils.TWEEN.Interpolation.Bezier)
            .onStart(() => {
                this._tutorialHand.visible = true;
            })
            .onComplete(() => {
                this._tutorialHand.gotoAndStop("tap");
            });

        const c = new P3.utils.TWEEN.Tween(this._tutorialHand,  this._tutorialHandTweens)
            .to({x: this._tutorialHandEnd.x + 200}, 640)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .delay(200)
            .onComplete(() => {
                this._tutorialHand.gotoAndStop("default");
            });

        const d = new P3.utils.TWEEN.Tween(this._tutorialHand, this._tutorialHandTweens)
            .to({alpha: 0}, 340)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .delay(400)
            .onComplete(() => {
                this._tutorialHand.visible = false;
                this.animateTutorial();
            });

        a.chain(b);
        b.chain(c);
        c.chain(d);
        a.start();
    }

    onOptionButtonDown(button) {
        this.grab(button);

        if (!PIXI.utils.isMobile.any) {
            this._grabPoint = P3.input.mouse.clone();
        }
    }

    onOptionButtonUp(button) {
    }

    onOptionButtonClick(button) {
        if (this._focusSortTarget == null) {
            this._focusSortTarget = button;
            this._focusSortIndex = button.index;
        } else {
            this._focusSortTarget = null;
        }
    }

    onDocumentMouseUp(event) {
        // we must delay this logic so it works with the order of events coming in - we cant release before we have grabbed.
        setTimeout(() => {
            this._focusSortTarget = null;

            if (this._selected) {
                if (PIXI.utils.isMobile.any) {
                    this.release();
                } else {
                    if (!this._grabBound) {
                        const v = new P3.Vector3(this._grabPoint.x - P3.input.mouse.x, this._grabPoint.y - P3.input.mouse.y);
                        if (v.length > this._grabRadius) {
                            this.release();
                        } else {
                            this._grabBound = true;
                        }
                    } else {
                        this._grabBound = false;
                        this.release();
                    }
                }
                console.log(this._question.sort);
            }
        }, 0);
    }

    onDocumentMouseMove(event) {
    }

    onDocumentKeyDown(event) {
        if (this._focusSortTarget) {
            if (event.key === "Tab") {
                event.preventDefault();

                let index = this._focusSortTarget.index + 1;
                if (index > this._buttons.length - 1) {
                    index = 0;
                }

                let to;
                for (let i = 0; i < this._buttons.length; ++i) {
                    if (this._buttons[i].value === this._question.sort[index]) {
                        to = this._buttons[i];
                        break;
                    }
                }

                this._focusSortTarget.parent.addChild(this._focusSortTarget);
                to.parent.addChild(to);

                to.index = this._focusSortTarget.index;
                this._focusSortTarget.index = index;

                this._question.sort[index] = this._focusSortTarget.value;
                this._question.sort[to.index] = to.value;

                console.log(index, this._focusSortTarget.index);

                for (let i = 0; i < this._buttons.length; ++i) {
                    const button = this._buttons[i];
                    button.tabIndex = GelButton.TAB_INDEX_LEVEL_2 + button.index;
                }
            }
        }
    }
}